import React, { ReactNode, useMemo } from 'react';
import { useMeetingHubContext } from '../MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionViewCard from './MotionViewCard';
import DocumentViewCard from './DocumentViewCard';
import VotingSection from './VotingSection';
import RejectViewCard from './RejectViewCard';
import TechnicalIssueViewCard from './TechnicalIssueViewCard';
import CommentViewCard from './CommentViewCard';
import ApprovalLevelViewCard from './ApprovalLevelViewCard';
import IPCheckVotingViewCard from './IPCheckVotingViewCard';
import TechnologyConflictViewCard from './TechnologyConflictViewCard';
import IPCheckSessionViewCard from './IPCheckSessionViewCard';
import CopyrightedSummaryViewCard from './CopyrightedSummaryViewCard';
import TrademarkViewCard from './TrademarkViewCard';
import IPCheckCompletionViewCard from './IPCheckCompletionViewCard';
import { displayTrademarkStatement } from '../MeetingStaffView/BallotReviewPanel/WrapUpActions/IPCheckSection/TrademarkSection/TrademarkSection';
import { displayCompletionConditionStatement } from '../MeetingStaffView/BallotReviewPanel/WrapUpActions/IPCheckSection/IPCheckCompletionSection/IPCheckCompletionSection';

const MeetingSessionPanel: React.FunctionComponent = () => {
  const { meetingSession, canViewAllVoters } = useMeetingHubContext();

  const gridItems = useMemo(() => {
    const items: ReactNode[] = [];
    const currentIpCheckSession = meetingSession?.currentIpCheckSession;
    const isTrademarkDisplayActive =
      currentIpCheckSession?.displayTrademark || currentIpCheckSession?.statement === displayTrademarkStatement;
    const isTrademarkCardActive = isTrademarkDisplayActive || !!currentIpCheckSession?.hasTrademark;
    const isCompletionDisplayActive =
      currentIpCheckSession?.displayCompletionCondition ||
      currentIpCheckSession?.statement === displayCompletionConditionStatement;
    const isCompletionCardActive = isCompletionDisplayActive || !!currentIpCheckSession?.completionConditionCheck;
    const isCopyrightedSummaryActive = !!currentIpCheckSession?.copyrightSummary;

    if (meetingSession?.currentDocument) {
      items.push(
        <GridItem key="document">
          <DocumentViewCard document={meetingSession.currentDocument} />
        </GridItem>
      );
    }

    if (meetingSession?.currentIpCheckVoting) {
      const item = (
        <GridItem key="ip-check-voting">
          <IPCheckVotingViewCard ipCheckVoting={meetingSession.currentIpCheckVoting} />
        </GridItem>
      );

      canViewAllVoters ? items.unshift(item) : items.push(item);
    }

    if (
      currentIpCheckSession &&
      !meetingSession.currentTechnologyConflict &&
      !isTrademarkCardActive &&
      !isCompletionCardActive &&
      !isCopyrightedSummaryActive
    ) {
      items.push(
        <GridItem key="ip-check-session">
          <IPCheckSessionViewCard ipCheckSession={currentIpCheckSession} />
        </GridItem>
      );
    }

    if (meetingSession?.currentTechnologyConflict) {
      items.push(
        <GridItem key="technology-conflict">
          <TechnologyConflictViewCard technology={meetingSession.currentTechnologyConflict} />
        </GridItem>
      );
    }

    if (currentIpCheckSession?.copyrightSummary) {
      items.push(
        <GridItem key="copyrighted-summary">
          <CopyrightedSummaryViewCard ipCheckSession={currentIpCheckSession} />
        </GridItem>
      );
    }

    if (meetingSession?.currentReject) {
      items.push(
        <GridItem key="reject">
          <RejectViewCard reject={meetingSession.currentReject} />
        </GridItem>
      );
    }

    if (meetingSession?.currentTechnicalIssue) {
      items.push(
        <GridItem key="technical-issues">
          <TechnicalIssueViewCard technicalIssue={meetingSession.currentTechnicalIssue} />
        </GridItem>
      );
    }

    if (meetingSession?.currentComment) {
      items.push(
        <GridItem key="comment">
          <CommentViewCard comment={meetingSession.currentComment} />
        </GridItem>
      );
    }

    if (isTrademarkCardActive) {
      items.push(
        <GridItem key="trademark">
          <TrademarkViewCard ipCheckSession={currentIpCheckSession} />
        </GridItem>
      );
    }

    if (isCompletionCardActive) {
      items.push(
        <GridItem key="ip-check-completion">
          <IPCheckCompletionViewCard ipCheckSession={currentIpCheckSession} />
        </GridItem>
      );
    }

    if (meetingSession?.currentMotion) {
      items.push(
        <GridItem key="motion">
          <MotionViewCard
            motion={meetingSession.currentMotion}
            votingResult={meetingSession.currentVotingSession?.result}
          />
        </GridItem>
      );
    }

    if (meetingSession?.currentApprovalLevel) {
      items.push(
        <GridItem key="approval-level">
          <ApprovalLevelViewCard approvalLevel={meetingSession.currentApprovalLevel} />
        </GridItem>
      );
    }

    if (meetingSession?.currentVotingSession) {
      const item = (
        <GridItem key="voting-section">
          <VotingSection votingSession={meetingSession.currentVotingSession} />
        </GridItem>
      );

      canViewAllVoters ? items.unshift(item) : items.push(item);
    }

    return items;
  }, [canViewAllVoters, meetingSession]);

  return <GridContainer>{gridItems}</GridContainer>;
};

export default MeetingSessionPanel;
